import * as React from 'react';
import { ButtonList, ComplexValue, ValidationProps } from 'ui';
import { ScheduleFieldKeys } from 'app/area/company-report/constants';
import { api } from '../api';
import { useEffect, useState } from 'react';
import { ScheduleDefinition } from 'app/area/company-report/types-store';
import { updateWithRangeEndAfterOccurences, updateWithRangeEndByDate, updateWithRangeOfRecurrenceOption,
  updateWithRangeStartDay, updateWithExecutionTime, onChangeCreator } from './schedule-helpers';
import { ScheduleRecurrencePatternTabs } from './schedule-form-recurrence-pattern-tabs';
import { ScheduleExecutionTime } from './schedule-form-execution-time';
import { ScheduleRecurrenceRange } from './schedule-form-recurrence-range';
import { SchedulePrimaryOwner } from './schedule-form-primary-owner';
import { deepEquals } from 'app/infrastructure/js';

interface ScheduleFormProps {
  scheduleDefinition: ScheduleDefinition;
  onUpdateScheduleDefinition: (scheduleDefinition: ScheduleDefinition, validateSchedule: boolean) => void;
  locale: string;
  translation: any;
  getValidationErrors: (key: string) => ValidationProps;
  primaryOwnerLabel?: string;
  primaryOwnerInfoMessage?: string;
  primaryOwnerPlaceholder?: string;
  initialOwners?: ComplexValue<number>[];
}

export const ScheduleForm = ({
  scheduleDefinition,
  onUpdateScheduleDefinition,
  locale,
  translation,
  getValidationErrors,
  primaryOwnerLabel,
  primaryOwnerInfoMessage,
  primaryOwnerPlaceholder,
  initialOwners = []
}: ScheduleFormProps) => {

  const [workingScheduleDefinition, setScheduleDefinition] = useState<ScheduleDefinition>(scheduleDefinition);
  const [recurrencePatternOptions, setRecurrencPatternOptions] = useState([]);
  const [scheduleWeekdayOptions, setScheduleWeekdayOptions] = useState([]);
  const [scheduleMonthOptions, setScheduleMonthOptions] = useState([]);
  const [scheduleOccurenceOptions, setScheduleOccurenceOptions] = useState([]);
  const [selectedRecurrencePattern, setRecurrencePattern] = useState(undefined);

  const getRecurrencePatternOptions = async () => {
    const options = await api.getRecurrencePatternOptions();
    setRecurrencPatternOptions(options);
    return options;
  };

  const getInitialOptions = async () => {
    setScheduleWeekdayOptions((await api.getScheduleWeekDays()));
    setScheduleMonthOptions((await api.getScheduleMonths()));
    setScheduleOccurenceOptions((await api.getScheduleOccurences()));
  };

  useEffect(() => {
    if (!!workingScheduleDefinition && !!scheduleDefinition && !deepEquals(workingScheduleDefinition, scheduleDefinition)) {
      onUpdateScheduleDefinition(workingScheduleDefinition, false);
    }
  }, [workingScheduleDefinition, onUpdateScheduleDefinition, scheduleDefinition]);
 
  useEffect(() => { 
    setRecurrencePattern(scheduleDefinition?.recurrencePattern);
    }, [recurrencePatternOptions, scheduleDefinition?.recurrencePattern]
  );

  useEffect(() => {
    getInitialOptions();
  }, []);

  const onChangeRecurrencePattern = (val: ComplexValue<number>) => {
    if (!workingScheduleDefinition) {
      return;
    }
    setRecurrencePattern(val);
    setScheduleDefinition(prevState => ({ ...prevState, recurrencePattern: val }));
  };
  const onChangeRangeStartDay = onChangeCreator(updateWithRangeStartDay, setScheduleDefinition);
  const onChangeRangeOfRecurrenceOption = onChangeCreator(updateWithRangeOfRecurrenceOption, setScheduleDefinition);
  const onChangeEndAfterOccurences = onChangeCreator(updateWithRangeEndAfterOccurences, setScheduleDefinition);
  const onChangeRangeEndByDate = onChangeCreator(updateWithRangeEndByDate, setScheduleDefinition);
  const onChangeExecutionTime = onChangeCreator(updateWithExecutionTime, setScheduleDefinition);

  const recurPatternValidationClass = !!getValidationErrors(ScheduleFieldKeys.recurrencePattern) ? 'has-danger' : '';

  return !!scheduleDefinition && <>
    <div className="row">
      <div className="col-12">
        <ButtonList
          componentId={ScheduleFieldKeys.recurrencePattern}
          className={recurPatternValidationClass}
          valueItem={selectedRecurrencePattern}
          onChange={onChangeRecurrencePattern}
          options={recurrencePatternOptions}
          onLoadOptions={getRecurrencePatternOptions}
          validation={getValidationErrors(ScheduleFieldKeys.recurrencePattern)}
        />
      </div>
    </div>

    <ScheduleRecurrencePatternTabs
      selectedRecurrencePattern={selectedRecurrencePattern?.value}
      scheduleDefinition={scheduleDefinition}
      setScheduleDefinition={setScheduleDefinition}
      scheduleWeekdayOptions={scheduleWeekdayOptions}
      scheduleMonthOptions={scheduleMonthOptions}
      scheduleOccurenceOptions={scheduleOccurenceOptions}
      locale={locale}
      getValidationErrors={getValidationErrors}
      translations={translation}
    />

    <hr />

    <ScheduleExecutionTime
      scheduleDefinition={scheduleDefinition}
      onChangeExecutionTime={onChangeExecutionTime}
      onChangeRangeStartDay={onChangeRangeStartDay}
      locale={locale}
      getValidationErrors={getValidationErrors}
      translations={translation}
    />

    <ScheduleRecurrenceRange
      scheduleDefinition={scheduleDefinition}
      onChangeRangeOfRecurrenceOption={onChangeRangeOfRecurrenceOption}
      onChangeEndAfterOccurences={onChangeEndAfterOccurences}
      onChangeRangeEndByDate={onChangeRangeEndByDate}
      locale={locale}
      getValidationErrors={getValidationErrors}
      translations={translation}
    />

    <SchedulePrimaryOwner
      initialOwners={initialOwners}
      scheduleOwner={scheduleDefinition?.scheduleOwner}
      primaryOwnerLabel={primaryOwnerLabel}
      primaryOwnerInfoMessage={primaryOwnerInfoMessage}
      primaryOwnerPlaceholder={primaryOwnerPlaceholder}
    />
  </>;
};
